import { Condition, ConditionGroup, ConditionOperator, NONVALUE_OPERATORS } from "@/schema"
import { nanoid } from "nanoid"
import { create } from "zustand"

function newAndGroup(): ConditionGroup {
  return { id: nanoid(), combinator: "and", conditions: [] }
}

function initialConditionGroup(): ConditionGroup {
  return { combinator: "or", conditions: [], child_groups: [newAndGroup()] }
}

export function conditionIsComplete(condition: Condition): boolean {
  if (!condition.operator) return false
  if (NONVALUE_OPERATORS.includes(condition.operator as ConditionOperator)) return true

  return !!(condition.value?.trim() || condition.values?.length)
}

export function normalizeReportConditionGroup(conditionGroup: ConditionGroup): ConditionGroup {
  return {
    combinator: "or",
    conditions: [],
    child_groups: (conditionGroup.child_groups || [])
      .map((group) => ({
        combinator: "and" as const,
        conditions: group.conditions.filter(conditionIsComplete),
      }))
      .filter((group) => group.conditions.length > 0),
  }
}

interface ReportConditionState {
  conditionGroup: ConditionGroup
  initialized: boolean
  reset: () => void
  hydrate: (conditionGroup?: ConditionGroup) => void
  addGroup: () => void
  removeGroup: (groupId: string) => void
  setGroupConditions: (groupId: string, conditions: Condition[]) => void
}

export const useReportConditionStore = create<ReportConditionState>((set) => ({
  conditionGroup: initialConditionGroup(),
  initialized: false,
  reset: () => set({ conditionGroup: initialConditionGroup(), initialized: true }),
  hydrate: (conditionGroup) =>
    set({
      initialized: true,
      conditionGroup: {
        combinator: "or",
        conditions: [],
        child_groups: conditionGroup?.child_groups?.map((group) => ({
          ...group,
          id: group.id || nanoid(),
          combinator: "and",
          conditions: (group.conditions || []).map((condition) =>
            condition.id ? condition : { ...condition, id: nanoid() }
          ),
        })) || [newAndGroup()],
      },
    }),
  addGroup: () =>
    set((state) => ({
      conditionGroup: {
        ...state.conditionGroup,
        child_groups: [...(state.conditionGroup.child_groups || []), newAndGroup()],
      },
    })),
  removeGroup: (groupId) =>
    set((state) => {
      const remainingGroups = (state.conditionGroup.child_groups || []).filter((group) => group.id !== groupId)
      return {
        conditionGroup: {
          ...state.conditionGroup,
          child_groups: remainingGroups.length > 0 ? remainingGroups : [newAndGroup()],
        },
      }
    }),
  setGroupConditions: (groupId, conditions) =>
    set((state) => ({
      conditionGroup: {
        ...state.conditionGroup,
        child_groups: (state.conditionGroup.child_groups || []).map((group) =>
          group.id === groupId ? { ...group, conditions } : group
        ),
      },
    })),
}))
